// list-items-small
import { Button, FestiveButton, HeadingTag, ModuleBase, ParallaxAnimation, ResponsiveImage } from '@/components';
import { selectIsCampaignActive } from '@/store';
import { isColorDark, themeFromBg } from '@/utils';
import Flicking from '@egjs/react-flicking';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const ListItemSmall = ({ data, ...props }) => {
  const campaignActive = useSelector(selectIsCampaignActive);
  const [width, setWidth] = useState(0);
  const [mouseOver, setMouseOver] = useState(null);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    setIsSafari(
      navigator.userAgent.includes('Safari') ||
        (navigator.userAgent.includes('Chrome') && navigator.platform.toLowerCase().includes('mac')),
    );

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const imageSizes = {
    widths: {
      2: { xs: 608, sm: 736, md: 482, lg: 674, xl: 674, xxl: 674 },
      3: { xs: 323, sm: 484, md: 317, lg: 445, xl: 445, xxl: 445 },
    },
    heights: { xs: 450, sm: 450, md: 350, lg: 527, xl: 527, xxl: 527 },
  };

  return (
    <ModuleBase
      data={data}
      {...props}
      theme={themeFromBg(!isColorDark(data.backgroundColour))}
      className={campaignActive && data.campaignMode ? 'campaign' : ''}
    >
      <div className={classNames('container', data.items.length > 2 && width < 768 ? 'pr-0' : '')}>
        {(data.preHeading || data.title) && (
          <div className="text-center max-w-[773px] mx-auto color-from-bg mb-12">
            {data.preHeading && <p className="text-sm leading-4 -tracking-[0.02em] mb-6">{data.preHeading}</p>}
            {data.headingTitle && data.headingTitle.heading && (
              <HeadingTag
                data={data.headingTitle}
                className="text-headingReg -tracking-[0.01em] md:text-title-l font-ivy-ora md:leading-60 md:-tracking-[0.03em]"
              ></HeadingTag>
            )}
          </div>
        )}
        {data.items && data.items.length ? (
          data.items.length > 2 && width < 768 ? (
            <Flicking panelsPerView={1.01} align="prev" className="overflow-visible pr-4">
              {data.items.map((item, index) => (
                <div
                  key={`listItem-${index}`}
                  className="listItem relative w-full h-[450px] group/item cursor-pointer overflow-hidden mr-3"
                  onMouseEnter={() => setMouseOver(index)}
                  onMouseLeave={() => setMouseOver(null)}
                >
                  <ParallaxAnimation offset={100} scale="scale-[1.5] sm:scale-1.5">
                    <ResponsiveImage
                      image={item.image}
                      widths={imageSizes.widths[data.items.length]}
                      heights={imageSizes.heights}
                      className={`block after:content-[''] after:absolute after:inset-0 after:size-full after:block after:bg-primary/70 after:transition-colors after:duration-300 group-hover/item:after:bg-white/90`}
                    />
                  </ParallaxAnimation>
                  <div className="absolute inset-0 py-5">
                    <div className="px-10 h-full flex flex-col justify-end text-white items-center group-hover/item:text-primary">
                      {item.description ? (
                        <div className="grid grid-rows-[max-content_0fr] group-hover/item:grid-rows-[max-content_1fr] transition-all duration-500 ease-out">
                          <p
                            className={classNames(
                              'text-title-xl leading-[5.25rem] text-center font-light -tracking-[0.04em] font-ivy-ora md:text-title-l md:leading-60 md:-tracking-[0.03em] lg:text-title-xxl lg:leading-120 lg:-tracking-[0.04em] transition-colors duration-300',
                              isSafari ? 'mb-6' : 'mb-3',
                            )}
                          >
                            {item.headingTitle}
                          </p>
                          <div className="overflow-hidden">
                            <div
                              className="text-large-paragraph text-center pb-3"
                              dangerouslySetInnerHTML={{ __html: item.description }}
                            />
                          </div>
                        </div>
                      ) : (
                        <p
                          className={classNames(
                            'text-title-xl leading-[5.25rem] font-light text-center -tracking-[0.04em] font-ivy-ora md:text-title-l md:leading-60 md:-tracking-[0.03em] lg:text-title-xxl lg:leading-120 lg:-tracking-[0.04em] transition-colors duration-300',
                            isSafari ? 'mb-6' : 'mb-3',
                          )}
                        >
                          {item.headingTitle}
                        </p>
                      )}
                      {item.primaryCta ? (
                        campaignActive && data.campaignMode ? (
                          <FestiveButton link={item.primaryCta}>{item.primaryCta.text}</FestiveButton>
                        ) : (
                          <Button
                            link={item.primaryCta}
                            theme={mouseOver === index ? 'dark' : 'light'}
                            type="primary"
                          />
                        )
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </Flicking>
          ) : (
            <div className="flex flex-col md:flex-row gap-3">
              {data.items.map((item, index) => (
                <div
                  key={`listItem-${index}`}
                  className="listItem relative w-full h-[450px] md:h-[350px] lg:h-[527px] overflow-hidden group/item cursor-pointer"
                  onMouseEnter={() => setMouseOver(index)}
                  onMouseLeave={() => setMouseOver(null)}
                >
                  <ParallaxAnimation
                    offset={100}
                    scale={
                      data.items.length > 2
                        ? 'md:scale-[1.75] lg:scale-125'
                        : 'h-[450px] scale-[1.25] md:h-[350px] lg:h-[527px]'
                    }
                  >
                    <ResponsiveImage
                      image={item.image}
                      widths={imageSizes.widths[data.items.length]}
                      heights={imageSizes.heights}
                      className={`block after:content-[''] after:absolute after:inset-0 after:size-full after:block after:bg-primary/70 after:transition-colors after:duration-300 group-hover/item:after:bg-white/90`}
                    />
                  </ParallaxAnimation>
                  <div className="absolute inset-0 py-6">
                    <div
                      className={classNames(
                        'h-full flex flex-col justify-end text-white mx-auto items-center group-hover/item:text-primary',
                        data.items.length === 2
                          ? 'px-10 md:px-0 md:max-w-[271px] lg:max-w-[334px]'
                          : 'lg:max-w-[322px]',
                      )}
                    >
                      {item.description ? (
                        <div className="grid grid-rows-[max-content_0fr] group-hover/item:grid-rows-[max-content_1fr] transition-all duration-500 ease-out">
                          <p
                            className={classNames(
                              'text-title-xl leading-[5.25rem] text-center font-light -tracking-[0.04em] font-ivy-ora md:text-title-l md:leading-60 md:-tracking-[0.03em] lg:text-title-xl lg:leading-[5.25rem] lg:-tracking-[0.04em] transition-all duration-300',
                              isSafari ? 'mb-6' : 'mb-3',
                            )}
                          >
                            {item.headingTitle}
                          </p>
                          <div className="overflow-hidden">
                            <div
                              className={classNames(
                                'text-large-paragraph text-center pb-3 lg:pb-4',
                                data.items.length === 2 ? '' : 'md:max-w-[171px] lg:max-w-none',
                              )}
                              dangerouslySetInnerHTML={{ __html: item.description }}
                            />
                          </div>
                        </div>
                      ) : (
                        <p
                          className={classNames(
                            'text-title-xl leading-[5.25rem] font-light text-center -tracking-[0.04em] font-ivy-ora md:text-title-l md:leading-60 md:-tracking-[0.03em] lg:text-title-xl lg:leading-[5.25rem] lg:-tracking-[0.04em] transition-colors duration-300',
                            isSafari ? 'mb-6' : 'mb-3',
                          )}
                        >
                          {item.headingTitle}
                        </p>
                      )}
                      {item.primaryCta ? (
                        campaignActive && data.campaignMode ? (
                          <FestiveButton link={item.primaryCta}></FestiveButton>
                        ) : (
                          <Button
                            link={item.primaryCta}
                            theme={mouseOver === index ? 'dark' : 'light'}
                            type="primary"
                          />
                        )
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : null}
      </div>
    </ModuleBase>
  );
};

export default ListItemSmall;
